import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type {
  RecurringTaskTemplate,
  RecurringTaskExecution,
  RecurringTaskSchedule
} from '@shared/definitions/recurring-task.model';
import type { TaskCard } from '@shared/definitions/task-card.model';



/**
 * Pattern B contract for Recurring Tasks.
 *
 * Manages templates (what + when), schedules (derived upcoming instances),
 * and executions (historical runs).
 */
export interface IRecurringTasksStore {
  // --- Raw state ---
  readonly templates: Signal<readonly RecurringTaskTemplate[]>;
  readonly executions: Signal<readonly RecurringTaskExecution[]>;
  readonly schedules: Signal<readonly RecurringTaskSchedule[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  // --- Derived state ---
  readonly activeTemplates: Signal<readonly RecurringTaskTemplate[]>;
  readonly totalTemplates: Signal<number>;

  // --- Actions ---
  refresh(): Promise<void>;

  createTemplate(
    template: Omit<RecurringTaskTemplate, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<RecurringTaskTemplate>;
  updateTemplate(templateId: string, updates: Partial<RecurringTaskTemplate>): Promise<void>;
  deleteTemplate(templateId: string): Promise<void>;

  getTemplateById(templateId: string): RecurringTaskTemplate | null;
  getTemplatesForProperty(propertyUID: string): readonly RecurringTaskTemplate[];

  getDueExecutions(): readonly RecurringTaskSchedule[];
  getUpcomingExecutions(daysAhead?: number): readonly RecurringTaskSchedule[];

  createTaskFromTemplate(
    template: RecurringTaskTemplate,
    propertyUID: string,
    scheduledDate: Date,
    propertyName?: string
  ): Promise<TaskCard>;

  skipExecution(executionId: string, reason: string): Promise<void>;

  getTemplate(templateId: string): Promise<RecurringTaskTemplate | undefined>;
}

export const RECURRING_TASKS_STORE = new InjectionToken<IRecurringTasksStore>(
  'RECURRING_TASKS_STORE');

export type RecurringTasksStoreMode = 'mock' | 'firestore';

export function resolveRecurringTasksStoreMode(): RecurringTasksStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('recurringTasks.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('recurringTasks.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
